import { AnimatePresence, motion } from 'framer-motion';
import * as _ from 'lodash-es';
import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Caret from './Caret';
import DropdownMenu, {
  useDropdownEventListener,
  useScrollItemIntoView,
} from './DropdownMenu';
import EmptyState from './EmptyState';
import * as styles from './ExpandableDropdown.module.scss';
import IconButton from './IconButton';

const MAX_ITEMS_TO_SHOW = 8;

interface SectionItem {
  id: string;
}

interface Section<Item extends SectionItem> {
  label: string | null;
  isExpandable: boolean;
  items: Item[];
  createItem?: Item;
  preferredNumberOfItems?: number;
}

type InferSectionItem<S> = S extends Section<infer Item>[] ? Item : never;

interface DisplaySection<Item extends SectionItem> extends Section<Item> {
  displayItems: Item[];
  startIndex: number;
}

type RenderItem<Item extends SectionItem> = (
  item: Item,
  state: { isFocused: boolean },
) => React.ReactNode;

interface InnerContextValue {
  index: number | null;
  displaySections: DisplaySection<SectionItem>[];
  expandedSection: Section<SectionItem> | null;
  setExpandedSectionIndex: React.Dispatch<React.SetStateAction<number | null>>;
  hasInteractedWithKeyboard: boolean;
}

const RenderItemContext = React.createContext<RenderItem<SectionItem>>(
  () => null,
);
const InnerContext = React.createContext<InnerContextValue | null>(null);
const SetInputElementContext = React.createContext<
  React.Dispatch<React.SetStateAction<HTMLInputElement | null>>
>(() => {});
const InputElementContext = React.createContext<HTMLInputElement | null>(null);
const SetIndexContext = React.createContext<
  React.Dispatch<React.SetStateAction<number>>
>(() => {});
const HandleSelectItemContext = React.createContext<
  (item: SectionItem) => void
>(() => {});

const useInnerContext = () => {
  const context = useContext(InnerContext);
  if (!context) {
    throw new Error('useInnerContext must be used within InnerContext');
  }
  return context;
};

interface CaretButtonProps {
  ariaLabel: string;
  onClick: React.MouseEventHandler;
  direction: 'left' | 'right';
}

const CaretButton = ({ ariaLabel, onClick, direction }: CaretButtonProps) => {
  const inputElement = useContext(InputElementContext);
  const inputHadFocusRef = useRef(false);
  return (
    <IconButton
      className={styles.iconButton}
      onMouseDown={() => {
        inputHadFocusRef.current = inputElement === document.activeElement;
      }}
      onClick={(e) => {
        onClick?.(e);
        if (inputHadFocusRef.current) {
          inputElement?.focus();
        }
      }}
      aria-label={ariaLabel}
    >
      <Caret direction={direction} size={12} />
    </IconButton>
  );
};

type IntermediateDisplaySection<Item extends SectionItem> = Omit<
  DisplaySection<Item>,
  'startIndex' | 'displayItems'
> & {
  displayCount: number;
  potentialDisplayCount: number;
};

export function balanceSections<Item extends SectionItem>({
  maxItemsToShow,
  sections,
}: {
  maxItemsToShow: number;
  sections: Section<Item>[];
}) {
  const { sum, displaySections } = sections.reduce(
    (acc, section, index) => {
      const remainingItemsCount = maxItemsToShow - acc.sum;
      const averageItemsCount = Math.ceil(
        remainingItemsCount / (sections.length - index),
      );
      const availableItemsCount = section.items.length;

      const maxCount =
        section.preferredNumberOfItems === 0
          ? 0
          : (section.preferredNumberOfItems ?? averageItemsCount);

      const displayCount =
        maxCount === 0
          ? 0
          : Math.min(availableItemsCount, maxCount, remainingItemsCount);

      return {
        displaySections: [
          ...acc.displaySections,
          {
            ...section,
            displayCount,
            potentialDisplayCount: displayCount,
          },
        ],
        sum: acc.sum + displayCount,
      };
    },
    {
      sum: 0,
      displaySections: [] as IntermediateDisplaySection<Item>[],
    },
  );

  const remaining = maxItemsToShow - sum;

  // Equally distribute remaining positions to categories,
  // or show categories that have preferredNumberOfItems of 0 if no other items
  _.range(remaining).forEach(() => {
    const sectionWithLeastItems = _.minBy(displaySections, (section) => {
      if (section.displayCount < section.items.length) {
        return section.displayCount;
      }
      return Infinity;
    });

    if (sectionWithLeastItems) {
      if (
        sectionWithLeastItems.items.length > sectionWithLeastItems.displayCount
      ) {
        sectionWithLeastItems.displayCount += 1;
      }

      sectionWithLeastItems.potentialDisplayCount += 1;
    }
  });

  return displaySections.reduce((acc, section) => {
    const previousSection = _.last(acc);
    const startIndex = previousSection
      ? previousSection.startIndex + previousSection.displayItems.length
      : 0;

    const items = section.items.slice(0, section.displayCount);

    const displayItems =
      section.createItem && items.length < section.potentialDisplayCount
        ? [...items, section.createItem]
        : items;

    const finalSection: DisplaySection<Item> = {
      displayItems,
      startIndex,
      items: section.items,
      label: section.label,
      preferredNumberOfItems: section.preferredNumberOfItems,
      createItem: section.createItem,
      isExpandable: section.items.length > 0 && section.isExpandable,
    };

    return [...acc, finalSection];
  }, [] as DisplaySection<Item>[]);
}

const TRANSITION = { type: 'tween', duration: 0.25 };

interface RootProps<S extends Section<SectionItem>[]> {
  sections: S;
  maxItemsToShow?: number;
  onSelect: (item: InferSectionItem<S>) => void;
  children: React.ReactNode;
  renderItem: (
    item: InferSectionItem<S>,
    state: {
      isFocused: boolean;
    },
  ) => React.ReactNode;
  onEscape?: () => void;
}

const Root = <S extends Section<SectionItem>[]>({
  sections,
  maxItemsToShow,
  onSelect,
  renderItem,
  children,
  onEscape,
}: RootProps<S>) => {
  const [inputElement, setInputElement] = useState<HTMLInputElement | null>(
    null,
  );
  const [index, setIndex] = useState(0);
  const [expandedSectionIndex, setExpandedSectionIndex] = useState<
    number | null
  >(null);

  const onSelectRef = useRef(onSelect);
  useLayoutEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  const renderItemRef = useRef(renderItem);
  useLayoutEffect(() => {
    renderItemRef.current = renderItem;
  }, [renderItem]);

  const previousIndexRef = useRef<number | null>(null);
  const currentIndexRef = useRef(index);
  useLayoutEffect(() => {
    currentIndexRef.current = index;
  }, [index]);

  useEffect(() => {
    if (expandedSectionIndex != null) {
      setIndex(0);
      previousIndexRef.current = currentIndexRef.current;
    } else if (previousIndexRef.current != null) {
      setIndex(previousIndexRef.current);
      previousIndexRef.current = null;
    }
  }, [expandedSectionIndex]);

  const expandedSection = sections[expandedSectionIndex ?? -1] ?? null;

  const displaySections = useMemo(
    () =>
      balanceSections({
        sections,
        maxItemsToShow: maxItemsToShow ?? MAX_ITEMS_TO_SHOW,
      }),
    [sections, maxItemsToShow],
  );

  const totalItems = useMemo(() => {
    if (expandedSection) {
      return expandedSection.items.length;
    }

    const lastSection = _.last(displaySections);

    return lastSection
      ? lastSection.startIndex + lastSection.displayItems.length
      : 0;
  }, [displaySections, expandedSection]);

  useEffect(() => {
    if (index == null) return;
    if (index >= totalItems) {
      setIndex(Math.max(totalItems - 1, 0));
    }
  }, [index, totalItems]);

  const { hasInteractedWithKeyboard } = useDropdownEventListener({
    inputElement,
    index,
    totalItems,
    onIndexChange: setIndex,
    onSelect() {
      const currentItem =
        index != null
          ? _.flatten(displaySections.map(({ displayItems }) => displayItems))[
              index
            ]
          : null;

      if (currentItem) {
        onSelect(currentItem as InferSectionItem<S>);
      }
    },
    onEscape,
  });

  const contextValue = useMemo((): InnerContextValue => {
    return {
      index,
      displaySections,
      expandedSection,
      setExpandedSectionIndex,
      hasInteractedWithKeyboard,
    };
  }, [expandedSection, displaySections, index, hasInteractedWithKeyboard]);

  const memoizedRenderItem = useCallback<RenderItem<SectionItem>>(
    (item, state) => renderItemRef.current(item as InferSectionItem<S>, state),
    [],
  );

  const handleSelectItem = useCallback((item: SectionItem) => {
    onSelectRef.current(item as InferSectionItem<S>);
  }, []);

  return (
    <HandleSelectItemContext.Provider value={handleSelectItem}>
      <SetIndexContext.Provider value={setIndex}>
        <RenderItemContext.Provider value={memoizedRenderItem}>
          <SetInputElementContext.Provider value={setInputElement}>
            <InputElementContext.Provider value={inputElement}>
              <InnerContext.Provider value={contextValue}>
                {children}
              </InnerContext.Provider>
            </InputElementContext.Provider>
          </SetInputElementContext.Provider>
        </RenderItemContext.Provider>
      </SetIndexContext.Provider>
    </HandleSelectItemContext.Provider>
  );
};

const Menu = () => {
  const expandedSectionRef = useRef<HTMLDivElement | null>(null);
  const [containerElement, setContainerElement] =
    useState<HTMLDivElement | null>(null);
  const [height, setHeight] = useState<number | undefined>(undefined);
  const [emptyStateHeight, setEmptyStateHeight] = useState<number | undefined>(
    undefined,
  );
  const {
    index,
    setExpandedSectionIndex,
    displaySections,
    expandedSection,
    hasInteractedWithKeyboard,
  } = useInnerContext();

  const renderItem = useContext(RenderItemContext);
  const setIndex = useContext(SetIndexContext);
  const handleSelectItem = useContext(HandleSelectItemContext);

  const focusedItemRef = useRef<HTMLButtonElement>(null);

  const containerRef = useMemo(
    () => ({ current: containerElement }),
    [containerElement],
  );

  useScrollItemIntoView({
    index,
    containerRef,
    focusedItemRef,
  });

  const isExpandedSectionEmpty =
    expandedSection !== null && expandedSection.items.length === 0;

  useEffect(() => {
    const expandedSectionEl = expandedSectionRef.current;
    if (!expandedSectionEl) return undefined;

    if (isExpandedSectionEmpty) {
      const resizeObserver = new ResizeObserver(() => {
        setEmptyStateHeight(expandedSectionEl.clientHeight);
      });

      resizeObserver.observe(expandedSectionEl);

      return () => resizeObserver.disconnect();
    }

    setEmptyStateHeight(undefined);
    return undefined;
  }, [isExpandedSectionEmpty]);

  useEffect(() => {
    if (!containerElement || expandedSection != null) return undefined;

    const resizeObserver = new ResizeObserver(() => {
      setHeight(containerElement.clientHeight);
    });

    resizeObserver.observe(containerElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, [containerElement, expandedSection]);

  return (
    <div
      style={{ height: emptyStateHeight ?? height }}
      className={styles.container}
    >
      <motion.div
        key="displaySections"
        ref={setContainerElement}
        transition={TRANSITION}
        style={{ position: 'absolute', width: '100%' }}
        animate={
          expandedSection ? { x: '-100%', opacity: 0 } : { x: 0, opacity: 1 }
        }
      >
        {displaySections.every(
          (section) => section.displayItems.length === 0,
        ) && (
          <div className={styles.emptyStateContainer}>
            <EmptyState
              message="No results found"
              imageName="notFound"
              smallText
              vertical
              noMargin
            />
          </div>
        )}

        {displaySections.map((section, sectionIndex) => {
          if (section.displayItems.length === 0) return null;

          return (
            // eslint-disable-next-line react/no-array-index-key
            <div className={styles.section} key={sectionIndex}>
              {!!section.label && (
                <DropdownMenu.SectionHeader
                  button={
                    section.isExpandable ? (
                      <CaretButton
                        onClick={() => setExpandedSectionIndex(sectionIndex)}
                        direction="right"
                        ariaLabel="Show all"
                      />
                    ) : null
                  }
                >
                  {section.label}
                </DropdownMenu.SectionHeader>
              )}

              {section.displayItems.map((item, itemIndex) => {
                const itemGlobalIndex = section.startIndex + itemIndex;

                const isFocused = index === itemGlobalIndex;

                return (
                  <DropdownMenu.Item
                    key={item.id}
                    hotkeys={
                      hasInteractedWithKeyboard ? new Set(['Enter']) : undefined
                    }
                    focused={isFocused}
                    onMouseEnter={() => setIndex(itemGlobalIndex)}
                    onClick={() => handleSelectItem(item)}
                    onMouseDown={(e) => e.preventDefault()}
                    ref={isFocused ? focusedItemRef : undefined}
                  >
                    {renderItem(item, { isFocused })}
                  </DropdownMenu.Item>
                );
              })}
            </div>
          );
        })}
      </motion.div>
      <AnimatePresence>
        {expandedSection && (
          <motion.div
            key="expandedSection"
            style={{ position: 'absolute', width: '100%' }}
            exit={{ x: '100%', opacity: 0 }}
            initial={{ x: '100%', opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={TRANSITION}
            className={styles.expandedSectionContainer}
          >
            <div ref={expandedSectionRef} className={styles.section}>
              <DropdownMenu.SectionHeader
                button={
                  <CaretButton
                    onClick={() => setExpandedSectionIndex(null)}
                    direction="left"
                    ariaLabel="Go back"
                  />
                }
              >
                {expandedSection.label}
              </DropdownMenu.SectionHeader>

              {expandedSection.items.length === 0 && (
                <div className={styles.emptyStateContainer}>
                  <EmptyState
                    message="No results found"
                    imageName="notFound"
                    smallText
                    vertical
                    noMargin
                  />
                </div>
              )}
              {expandedSection.items.map((item, itemIndex) => {
                const isFocused = index === itemIndex;

                return (
                  <DropdownMenu.Item
                    key={item.id}
                    hotkeys={
                      hasInteractedWithKeyboard ? new Set(['Enter']) : undefined
                    }
                    focused={isFocused}
                    onMouseEnter={() => setIndex(itemIndex)}
                    onClick={() => handleSelectItem(item)}
                    onMouseDown={(e) => e.preventDefault()}
                    ref={isFocused ? focusedItemRef : undefined}
                  >
                    {renderItem(item, { isFocused })}
                  </DropdownMenu.Item>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Input = (props: React.InputHTMLAttributes<HTMLInputElement>) => {
  const setInputElement = useContext(SetInputElementContext);
  return <input ref={setInputElement} {...props} />;
};

const ExpandableDropdown = {
  Root,
  Input,
  Menu,
};

export default ExpandableDropdown;
